import { useEffect, useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import Select, { SingleValue } from "react-select";
import { FaSpinner } from "react-icons/fa6";

import { useTransaction } from "../../../contexts/TransactionContext";
import {
  useGetPaymentProviders,
  useResolveAccountNumber,
} from "../../../services/queries/payments";
import PageTransition from "../../../components/PageTransition";
import {
  currenciesConfig,
  PaymentMethod,
  WithdrawalType,
  routePaths,
} from "../../../constants";
import BackButton from "../../../components/BackButton";
import {
  beneficiariesResourcePath,
  useGetBeneficiaries,
} from "../../../services/queries/beneficiaries";
import { useFilters } from "../../../hooks/useFilters";
import BeneficiaryList from "../../../components/BeneficiaryList";
import { Beneficiary } from "../../../services/queries/beneficiaries/types";
import { validateAccountName } from "../../../utils/payment";
import { requireRouteAccess } from "../../../utils/authorization";

export const Route = createFileRoute("/_app/send/beneficiary")({
  beforeLoad: () => requireRouteAccess("/send/beneficiary"),
  component: BeneficiaryPage,
});

function BeneficiaryPage() {
  const navigate = useNavigate();

  const { state: formData, updateTransaction } = useTransaction();
  const {
    accountName,
    accountNumber,
    bankCode,
    paymentMethod,
    withdrawalType,
    baseCurrency,
    quoteCurrency,
    saveBeneficiary,
  } = formData;

  const currency =
    withdrawalType === WithdrawalType.INTERNATIONAL
      ? quoteCurrency
      : baseCurrency;
  const currencyConfig = currenciesConfig.find(
    (item) => item.currency === currency,
  );

  // Validate if current payment method is valid for this currency
  const availablePaymentMethods = currencyConfig?.paymentMethods || [];
  const isCurrentPaymentMethodValid = availablePaymentMethods.some(
    (method) => method.value === paymentMethod,
  );

  // Use valid payment method or fallback to first available
  const activePaymentMethod = isCurrentPaymentMethodValid
    ? paymentMethod
    : availablePaymentMethods[0]?.value;

  const { data: paymentProvidersData, isLoading: isLoadingPaymentProviders } =
    useGetPaymentProviders(currencyConfig?.countryCode, activePaymentMethod);
  const paymentProviderList = paymentProvidersData?.data;
  const paymentProviders = useMemo<Array<{ value: string; label: string }>>(
    () =>
      paymentProviderList
        ? paymentProviderList.map((provider) => ({
            value: provider.code,
            label: provider.name,
          }))
        : [],
    [paymentProviderList],
  );
  const selectedPaymentProvider =
    paymentProviders.find((provider) => provider.value === bankCode) ?? null;

  const currencyPaymentMethod = currencyConfig?.paymentMethods.find(
    (method) => method.value === activePaymentMethod,
  );

  // Determine if we should resolve account number
  const canResolve = currencyPaymentMethod?.canResolveAccountName ?? false;
  const minLength = currencyPaymentMethod?.minLength ?? 0;
  const maxLength = currencyPaymentMethod?.maxLength ?? 30;

  // Only resolve when:
  // 1. Resolution is supported for this payment method
  // 2. Account number is within valid length range
  // 3. Bank code is selected (not stale)
  // 4. Bank code matches a valid provider for this currency
  const isValidBankCode = paymentProviders.some(
    (provider) => provider.value === bankCode,
  );
  const isValidLength =
    accountNumber.length >= minLength && accountNumber.length <= maxLength;
  const shouldResolveAccountNumber =
    canResolve && isValidLength && isValidBankCode && !!bankCode;

  const {
    data: resolvedAccountNumberData,
    isLoading: isResolvingAccountNumber,
  } = useResolveAccountNumber(
    accountNumber,
    bankCode,
    currencyConfig?.countryCode,
    shouldResolveAccountNumber,
  );
  const resolvedAccountName = resolvedAccountNumberData?.data;
  const { buildApiUrl } = useFilters({
    currency: currency,
  });
  const url = buildApiUrl(beneficiariesResourcePath);

  const { data: beneficiariesData, isLoading: isLoadingBeneficiaries } =
    useGetBeneficiaries(url);

  const beneficiaries = beneficiariesData?.data?.list || [];

  const [selectedBeneficiary, setSelectedBeneficiary] =
    useState<Beneficiary | null>(null);

  useEffect(() => {
    if (!selectedBeneficiary) return;
    updateTransaction({
      beneficiaryId: selectedBeneficiary.id,
      accountName: selectedBeneficiary.accountName,
      accountNumber: selectedBeneficiary.accountNumber,
      bankName:
        selectedBeneficiary.bankName || selectedBeneficiary.bankCode || "",
      bankCode: selectedBeneficiary.bankCode || "",
      paymentMethod: selectedBeneficiary.paymentMethod as PaymentMethod,
    });
  }, [selectedBeneficiary, updateTransaction]);

  const handlePaymentMethodChange = (
    method: SingleValue<(typeof availablePaymentMethods)[number]>,
  ) => {
    if (!method) return;
    updateTransaction({ paymentMethod: method.value });
  };

  const handlePaymentProviderChange = (
    provider: SingleValue<{ value: string; label: string }>,
  ) => {
    if (!provider) {
      updateTransaction({ bankCode: "", bankName: "", accountName: "" });
      return;
    }
    updateTransaction({
      bankCode: provider.value,
      bankName: provider.label,
      accountName: "",
    });
  };

  const handleAccountNumberChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    let value = e.target.value.replace(/\D/g, "");

    const hasCurrencyConfig =
      currencyConfig && Object.keys(currencyConfig).length > 0;

    if (paymentMethod === PaymentMethod.MOBILE_MONEY && hasCurrencyConfig) {
      const diallingCode = currencyConfig?.diallingCode;

      if (value.startsWith(diallingCode)) {
        value = "0" + value.slice(diallingCode.length);
      }
    }

    // reduce account number length to max allowed
    const maxLength = currencyConfig?.paymentMethods.find(
      (method) => method.value === paymentMethod,
    )?.maxLength;
    value = value.slice(0, maxLength);

    updateTransaction({ accountNumber: value, accountName: "" });
  };

  const handleAccountNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateTransaction({ accountName: e.target.value.toUpperCase() });
  };

  const handleSaveBeneficiaryToggle = () => {
    updateTransaction({ saveBeneficiary: !saveBeneficiary });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const submitData = formData.beneficiaryId
      ? {
          beneficiaryId: formData.beneficiaryId,
        }
      : {
          accountName: formData.accountName,
          accountNumber: formData.accountNumber,
          bankCode: formData.bankCode,
          bankName: formData.bankName,
          paymentMethod: formData.paymentMethod,
          saveBeneficiary: formData.saveBeneficiary,
        };

    updateTransaction(submitData);

    navigate({ to: routePaths.sendReview });
  };

  const paymentMethods = availablePaymentMethods;
  const selectedPaymentMethod =
    paymentMethods.find((method) => method.value === activePaymentMethod) ??
    null;

  // Sync active payment method to state and clear stale data when currency changes
  useEffect(() => {
    if (!activePaymentMethod) {
      return;
    }

    // Check if bankCode is valid for current currency
    const isBankCodeValid = paymentProviders.some(
      (provider) => provider.value === bankCode,
    );

    // Build update payload
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const updates: any = {};

    // Update payment method if it changed
    if (paymentMethod !== activePaymentMethod) {
      updates.paymentMethod = activePaymentMethod;
    }

    // Clear stale bank data if bankCode is not valid for this currency
    if (bankCode && !isBankCodeValid) {
      updates.bankCode = "";
      updates.bankName = "";
      updates.accountName = "";
      updates.accountNumber = "";
    }

    // Only dispatch if there are updates
    if (Object.keys(updates).length > 0) {
      updateTransaction(updates);
    }
  }, [
    activePaymentMethod,
    currency,
    paymentProviders,
    bankCode,
    paymentMethod,
    updateTransaction,
  ]);

  useEffect(() => {
    if (!resolvedAccountName) {
      return;
    }

    // get bank name
    const bankName = paymentProviderList?.find(
      (provider) => provider.code === bankCode,
    )?.name;

    updateTransaction({
      accountName: resolvedAccountName,
      ...(bankName && { bankName }),
    });
  }, [resolvedAccountName, bankCode, paymentProviderList, updateTransaction]);

  const accountNameValidation = validateAccountName(
    accountName,
    currencyConfig?.countryCode,
  );
  const isInValidBeneficiaryForm =
    !accountNumber ||
    !accountName ||
    !bankCode ||
    !selectedPaymentMethod ||
    !accountNameValidation.isValid;

  return (
    <PageTransition>
      <div className="withdrawal-beneficiary-page bg-light min-h-screen text-gray-800 p-4">
        <div className="flex items-center mb-4">
          <BackButton />
          <h1 className="text-xl font-bold ml-10">Beneficiary</h1>
        </div>

        <div className="beneficiary-container mb-4">
          {isLoadingBeneficiaries ? (
            <div className="flex items-center justify-center gap-3 py-8">
              <FaSpinner className="animate-spin w-6 h-6 text-primary" />
              <span className="text-base font-medium text-gray-700">
                Loading beneficiaries...
              </span>
            </div>
          ) : beneficiaries && beneficiaries?.length > 0 ? (
            <>
              <h2 className="text-sm font-semibold mb-2 text-gray-800">
                Send again
              </h2>

              <BeneficiaryList
                data={beneficiaries}
                setSelectedBeneficiary={setSelectedBeneficiary}
              />
            </>
          ) : (
            <p className="text-sm text-gray-500">No beneficiaries found.</p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="beneficiary-form-field">
            <label className="block text-gray-700 mb-2" htmlFor="paymentMethod">
              Payment Method
            </label>

            <Select
              classNamePrefix="select"
              inputId="paymentMethod"
              value={selectedPaymentMethod}
              options={paymentMethods}
              onChange={handlePaymentMethodChange}
            />
          </div>

          <div className="beneficiary-form-field">
            <label className="block text-gray-700 mb-2" htmlFor="bankCode">
              {paymentMethod === PaymentMethod.BANK ? "Bank" : "Provider"}
            </label>

            <Select
              classNamePrefix="select"
              isClearable
              isSearchable
              inputId="bankCode"
              value={selectedPaymentProvider}
              options={paymentProviders}
              onChange={handlePaymentProviderChange}
              isLoading={isLoadingPaymentProviders}
            />
          </div>

          <div className="beneficiary-form-field">
            <label className="block text-gray-700 mb-2" htmlFor="accountNumber">
              {paymentMethod === PaymentMethod.BANK
                ? "Account Number"
                : "Mobile Number"}
            </label>

            <input
              type="text"
              inputMode="numeric"
              id="accountNumber"
              name="accountNumber"
              value={accountNumber}
              onChange={handleAccountNumberChange}
              maxLength={maxLength}
              placeholder={
                minLength === maxLength
                  ? `Enter ${minLength} digit ${
                      activePaymentMethod === PaymentMethod.BANK
                        ? "account"
                        : "mobile"
                    } number`
                  : `Enter ${minLength}-${maxLength} digit ${
                      activePaymentMethod === PaymentMethod.BANK
                        ? "account"
                        : "mobile"
                    } number`
              }
              className="w-full p-3 border rounded-lg"
            />

            {accountNumber && !isValidLength && (
              <p className="text-red-500 text-sm mt-1">
                {minLength === maxLength
                  ? `Account number must be ${minLength} digits`
                  : `Account number must be between ${minLength} and ${maxLength} digits`}
              </p>
            )}
          </div>

          <div className="beneficiary-form-field">
            <label className="block text-gray-700 mb-2" htmlFor="accountName">
              Account name
            </label>

            <input
              type="text"
              id="accountName"
              name="accountName"
              value={accountName}
              onChange={handleAccountNameChange}
              disabled={withdrawalType === WithdrawalType.LOCAL}
              className="w-full p-3 border rounded-lg"
            />

            {isResolvingAccountNumber && (
              <div className="flex items-center gap-2 mt-1 text-sm text-gray-600">
                <FaSpinner className="animate-spin" />
                <span>Resolving account number...</span>
              </div>
            )}

            {accountName && accountNameValidation.warning && (
              <p className="text-amber-600 text-sm mt-1 flex items-start gap-1">
                <span className="text-amber-600">⚠️</span>
                <span>{accountNameValidation.warning}</span>
              </p>
            )}

            {accountName && !accountNameValidation.isValid && (
              <p className="text-red-500 text-sm mt-1">
                Please enter a valid account name
              </p>
            )}
          </div>

          <div className="beneficiary-form-field flex items-center">
            <label className="flex items-center cursor-pointer">
              <div className="relative">
                <input
                  id="saveBeneficiary"
                  name="saveBeneficiary"
                  type="checkbox"
                  className="sr-only"
                  checked={saveBeneficiary}
                  onChange={handleSaveBeneficiaryToggle}
                />
                <div
                  className={`w-14 h-8 rounded-full transition-colors ${
                    formData.saveBeneficiary ? "bg-green-500" : "bg-gray-300"
                  }`}
                >
                  <div
                    className={`absolute w-6 h-6 rounded-full bg-white top-1 transition-transform ${
                      saveBeneficiary ? "translate-x-7" : "translate-x-1"
                    }`}
                  />
                </div>
              </div>
              <span className="ml-3">Save beneficiary</span>
            </label>
          </div>

          <button
            type="submit"
            disabled={isInValidBeneficiaryForm}
            className="w-full p-4 bg-primary text-white rounded-lg mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Continue
          </button>
        </form>
      </div>
    </PageTransition>
  );
}
